/* eslint-disable react/display-name */
/* eslint-disable react/require-default-props */
/*
 * @Author: wangjiaxin@leedarson.com
 * @Date: 2019-08-20 12:11:25
 * Copyright © Leedarson. All rights reserved.
 */

import React, {
  useCallback,
  useMemo,
  useState,
  useEffect,
  useRef,
} from 'react';
import PropTypes from 'prop-types';
import Dialog from 'rmc-dialog';
import { getZIndex, increaseZIndex } from './zIndexHelper';
// import native from '@native';
import ModalButton from './ModalButton';
// import { fade, zoom, slideUp } from '../styledUtils';
import { EMPTY_FUNCTION } from './constants';
import { createModalSequence, removeModalSequence } from './modalSequences';

let _sequenceId = 1000;

const Modal = React.memo((props) => {
  const {
    visible,
    size,
    closable,
    confirm,
    cancel,
    footerColumn,
    primaryButtons,
    mask,
    sequenceId,
    maskClosable,
    position,
    afterClose,
    onConfirm,
    onActions,
    onCancel,
    lock,
    children,
    actions,
    wrapClassName,
    ...restProps
  } = props;

  const [modalVisible, setModalVisible] = useState(visible);
  const sequenceIdRef = useRef('');

  const zIndexRef = useRef(getZIndex());
  useEffect(() => {
    increaseZIndex();
  }, []);

  useEffect(() => {
    return () => {
      if (sequenceIdRef.current) {
        removeModalSequence(sequenceIdRef.current);
      }
    };
  }, []);

  useEffect(() => {
    // if (visible) {
    //   observer.notify('setNativeTabbarVisible', false, 'modal effect');
    // }
    setModalVisible(visible);
    // 弹框显示，禁用IOS手势返回
    // 关闭弹框，回到之前的IOS手指状态
    // if (!visible) {
    //   native.system.setNavigationGestures(
    //     native.system.getNavigationGestures(),
    //     false,
    //   );
    // } else {
    //   native.system.setNavigationGestures(false, false);
    // }
  }, [visible]);

  // 关闭弹框，回到之前的IOS手指状态
  useEffect(() => {
    // return () => {
    //   native.system.setNavigationGestures(
    //     native.system.getNavigationGestures(),
    //     false,
    //   );
    // };
  }, []);

  // wrap confirm click, support for async
  const wrapConfirmClick = useCallback(
    async (e, isCalledByCode) => {
      /* 
        1207 该部分代码导致弹窗中的输入框收起键盘后，失焦仍弹出键盘的问题，暂时注释
        需自测验证，穿透问题是否存在
      */
      // 解决穿透问题
      if (e) {
        e.preventDefault();
      }
      let result = true;
      if (!isCalledByCode) {
        result = await onConfirm();
      }

      if (result !== false) {
        setModalVisible(false);
        removeModalSequence(sequenceIdRef.current);
      }
    },
    [onConfirm],
  );

  const wrapActionsClick = useCallback(
    async (e, index) => {
      /* 
        1207 该部分代码导致弹窗中的输入框收起键盘后，失焦仍弹出键盘的问题，暂时注释
        需自测验证，穿透问题是否存在
      */
      // 解决穿透问题
      if (e) {
        e.preventDefault();
      }
      let result = true;
      if (onActions) {
        result = await onActions(index);
      }

      if (result !== false) {
        setModalVisible(false);
        removeModalSequence(sequenceIdRef.current);
      }
    },
    [onActions],
  );

  // wrap cancel click, support for async
  const wrapCancelClick = useCallback(
    (e, isCalledByCode) => {
      /* 
        1207 该部分代码导致弹窗中的输入框收起键盘后，失焦仍弹出键盘的问题，暂时注释
        需自测验证，穿透问题是否存在
      */
      // 解决穿透问题
      if (e) {
        e.preventDefault();
      }

      let result = true;
      if (!isCalledByCode) {
        result = onCancel();
      }
      if (result !== false) {
        setModalVisible(false);
        removeModalSequence(sequenceIdRef.current);
      }
    },
    [onCancel],
  );

  useEffect(() => {
    if (visible) {
      let id = sequenceId;
      if (!id) {
        _sequenceId += 1;
        id = `modal__${_sequenceId}`;
      }
      sequenceIdRef.current = id;
      createModalSequence(id, {
        close: () => {
          wrapCancelClick(null, true);
        },
        lock,
      });
    }
  }, [lock, sequenceId, visible, wrapCancelClick]);

  const onAfterClose = useCallback(() => {
    // observer.notify('setNativeTabbarVisible', true, 'modal afterClose');
    afterClose();
  }, [afterClose]);

  // modal footer
  const Footer = useMemo(() => {
    let footer = null;
    if (confirm || cancel) {
      footer = [];
      if (footer) {
        footer.push(
          <div
            className={`modal-footer-button ${footerColumn ? 'column' : ''}`}
            key={1}
          >
            {cancel && (
              <ModalButton
                key="cancel"
                type="cancel"
                primaryButtons={primaryButtons}
                onTouchEnd={wrapCancelClick}
              >
                {cancel}
              </ModalButton>
            )}
            {actions &&
              actions.length > 0 &&
              actions.map((i, index) => (
                <ModalButton
                  key={i}
                  type="confirm"
                  primaryButtons={primaryButtons}
                  onTouchEnd={(e) => wrapActionsClick(e, index)}
                >
                  {i}
                </ModalButton>
              ))}
            {confirm && (
              <ModalButton
                key="confirm"
                type="confirm"
                primaryButtons={primaryButtons}
                onTouchEnd={wrapConfirmClick}
              >
                {confirm}
              </ModalButton>
            )}
          </div>,
        );
      }
    }
    return footer;
  }, [
    confirm,
    cancel,
    footerColumn,
    primaryButtons,
    wrapCancelClick,
    actions,
    wrapConfirmClick,
    wrapActionsClick,
  ]);

  return (
    <>
      <Dialog
        zIndex={zIndexRef.current < 5000 ? 5000 : zIndexRef.current}
        prefixCls={Modal.prefixCls}
        className={`${Modal.prefixCls}-${position}`}
        wrapClassName={`${Modal.prefixCls}-wrap-${position} ${Modal.prefixCls}-wrap-${size} ${wrapClassName}`}
        visible={modalVisible}
        closable={closable}
        maskClosable={maskClosable}
        footer={Footer}
        afterClose={onAfterClose}
        // transitionName={position === 'bottom' ? slideUp : zoom}
        // maskTransitionName={fade}
        mask={mask}
        {...restProps}
      >
        {children}
      </Dialog>
    </>
  );
});

Modal.prefixCls = 'LdsModal';

Modal.defaultProps = {
  visible: false,
  size: 'medium',
  closable: false,
  confirm: '',
  cancel: '',
  mask: true,
  maskClosable: false,
  position: 'center',
  sequenceId: '',
  lock: false,
  afterClose: EMPTY_FUNCTION,
  onConfirm: EMPTY_FUNCTION,
  onCancel: EMPTY_FUNCTION,
  primaryButtons: ['confirm'],
  wrapClassName: '',
};

Modal.propTypes = {
  children: PropTypes.node.isRequired,
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  size: PropTypes.oneOf(['small', 'medium', 'large']),
  visible: PropTypes.bool,
  closable: PropTypes.bool,
  sequenceId: PropTypes.string,
  confirm: PropTypes.node,
  cancel: PropTypes.node,
  mask: PropTypes.bool,
  maskClosable: PropTypes.bool,
  position: PropTypes.oneOf(['ios', 'center', 'bottom']),
  lock: PropTypes.bool,
  afterClose: PropTypes.func,
  onConfirm: PropTypes.func,
  onCancel: PropTypes.func,
  primaryButtons: PropTypes.arrayOf(PropTypes.string),
  wrapClassName: PropTypes.string,
};

export default Modal;
